import React, {useCallback, useEffect} from 'react';

import {UserIdentificationQuery, useUserIdentificationQueryLazyQuery} from '#apollo/schema';
import {useAnalytics} from '#component/provider/AnalyticsProvider';
import {EventType} from '#component/provider/AnalyticsProvider';
import {UserIdentifiedEvent} from '#component/provider/AnalyticsProvider';
import {useAuth} from '#hook';

import {
  UserIdentificationProviderProps,
  UserIdentifiedEventPayload,
} from './UserIdentificationProvider.types';

const buildUserIdentifiedEventPayload = (
  userId: string,
  data: UserIdentificationQuery,
): UserIdentifiedEventPayload => {
  const email = data?.me.userProfile?.email;

  return {
    id: userId,
    ...(email ? {email} : {}),
    hasActiveSubscription: !!data?.me.subscription?.hasActiveSubscription,
    tallyAge: data?.me.mostRecentLabSubmissionWithResults?.labAssessment?.bioAge || 0,
    surveyScore:
      data?.me.mostRecentLabSubmissionWithResults?.userSurveySubmission?.surveyAssessment
        ?.lifestyleScore || 0,
    testNumber: data?.me.labSubmissionsWithResultCount || 0,
    hasSelectedActionPlan:
      !!data?.me.mostRecentLabSubmissionWithResults?.userSurveySubmission?.surveyAssessment
        ?.selectedActionPlan?.id,
  };
};

export const UserIdentificationProvider = ({children}: UserIdentificationProviderProps) => {
  const {track} = useAnalytics();
  const {isUserSignedIn} = useAuth();
  const [getUserIdentificationQuery] = useUserIdentificationQueryLazyQuery();

  const isSignedIn = isUserSignedIn();

  const handleIdentifyUser = useCallback(async () => {
    if (isSignedIn) {
      const {data} = await getUserIdentificationQuery();
      const userId = data?.me.id || '';

      if (data && userId && userId.length > 0) {
        // Adding explicit `.catch()` at the end to indicate that we indeed do
        // not want to wait for the promise to resolve.
        track<UserIdentifiedEvent>({
          evtName: EventType.UserIdentified,
          userId,
          data: buildUserIdentifiedEventPayload(userId, data),
        }).catch();
      }
    }
  }, [isSignedIn, getUserIdentificationQuery, track]);

  useEffect(() => {
    // Adding explicit `.catch()` at the end to indicate that we indeed do
    // not want to wait for the promise to resolve.
    handleIdentifyUser().catch();
  }, [handleIdentifyUser]);

  return <>{children}</>;
};
